import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'
import Cookies from 'js-cookie'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

// Function to check cookies and transfer selectedModule to sessionStorage
const checkAndTransferSelectedModule = (currentUser: UserModel | undefined) => {
  if (currentUser?.idUsuario) {
    const cookieKey = `selectedModule_user${currentUser.idUsuario}`;
    const savedModule = Cookies.get(cookieKey);
    
    if (savedModule) {
      // Transfer from cookie to sessionStorage
      sessionStorage.setItem('selectedModule', savedModule);
    }
  }
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    console.log("🚀 ~ saveAuth ~ auth:", auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    saveAuth(undefined)
    setCurrentUser(undefined)
    // Limpa dados temporários também
    sessionStorage.removeItem('tempUser')
    sessionStorage.removeItem('selectedModule')
  }

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async (apiToken) => {
      try {
        if (!didRequest.current) {
          const currentUser: any = JSON.parse(localStorage.getItem('user')!);
          // Verifica se o usuário existe e não tem senha expirada
          if (currentUser && !currentUser.trocarSenhaExpirada && !currentUser.trocarSenhaTemporaria) {
            setCurrentUser(currentUser)
            // Check and transfer selectedModule from cookies to sessionStorage
            checkAndTransferSelectedModule(currentUser)
          } else if (currentUser && (currentUser.trocarSenhaExpirada || currentUser.trocarSenhaTemporaria)) {
            // Se tem senha expirada, remove do localStorage e redireciona para login
            localStorage.removeItem('user')
            logout()
          }
        }
      } catch (error) {
        console.error(error)
        if (!didRequest.current) {
          localStorage.removeItem('user')
          sessionStorage.removeItem('tempUser')
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth) {
      requestUser(auth)
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
